import QueryContext from "state/query";
import {
  faChevronRight,
  faChevronDown,
  faLocationQuestion,
} from "@fortawesome/pro-light-svg-icons";
import { faList, faXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GipContext } from "state";
import Flag from "react-world-flags";

const { useCountries, useStatuses, useGTM } = require("hooks");
const { useContext, useState, useEffect } = require("react");

const Project = ({ id, name, city, status, countryId }) => {
  const { statuses } = useStatuses();
  const { hoverProject, setHoverProject } = useContext(GipContext);
  return (
    <div
      className="relative flex justify-between items-center"
      onMouseEnter={() => {
        setHoverProject(id, countryId);
      }}
      onMouseLeave={() => setHoverProject(null)}
    >
      <div>
        <p
          className="text-gray-800 font-bold text-sm"
          dangerouslySetInnerHTML={{ __html: name }}
        />
        <div className="flex gap-1">
          {city && (
            <p className="text-gray-400 text-sm">
              <span className="opacity-60">City: </span> {city}
            </p>
          )}
          <span className="text-gray-400 text-sm">・</span>
          <p className="text-gray-400 text-sm">
            <span className="opacity-60">Status: </span>{" "}
            {statuses?.find((s) => s.id === status)?.name}
          </p>
        </div>
      </div>
      <div>
        <FontAwesomeIcon
          icon={faChevronRight}
          className={`${
            hoverProject &&
            hoverProject.projectId === id &&
            hoverProject.countryId === countryId
              ? "!opacity-100 "
              : ""
          } text-primary font-bold opacity-0 group-hover:opacity-100`}
        />
      </div>
    </div>
  );
};

function groupProjectsByCountries(projects) {
  let results = {};
  projects.forEach((p) => {
    p?.acf?.locations?.forEach((l) => {
      if (!results.hasOwnProperty(l.country)) {
        results[l.country] = [];
      }
      const m = l.geolocation.markers[0];
      if (m) {
        results[l.country].push({
          name: p.title.rendered,
          position: [m.lat, m.lng],
          status: p["gpi-statuses"]?.[0] ?? null,
          city: l.city,
          id: p.id,
        });
      } else {
        console.error("Project " + p.title.rendered + " is missing marker");
      }
    });
  });
  return results;
}

const List = () => {
  const {
    gips,
    setActiveProject,
    showProjectList,
    setShowProjectList,
    map,
    isMobile,
  } = useContext(GipContext);
  const { countries: selectedCountries } = useContext(QueryContext);
  const { countries } = useCountries();
  const [dataByCountries, setDataByCountries] = useState([]);
  const [orderAlphabetically, setOrderAlphabetically] = useState(true);
  const { dataLayer } = useGTM();
  useEffect(() => {
    let result = [];
    if (gips?.length && countries?.length) {
      const data = groupProjectsByCountries(gips, countries);
      let validCountries = countries
        .filter((c) => data.hasOwnProperty(c.id))
        .filter(
          (c) =>
            selectedCountries.length === 0 || selectedCountries.includes(c.id)
        )
        .sort((a, b) => a.name - b.name);
      if (!orderAlphabetically) {
        validCountries = validCountries.reverse();
      }

      result = validCountries.map((c) => ({
        ...c,
        projects: data[c.id]
          //sort by name
          .sort((p1, p2) => {
            const n1 = p1.name.toLowerCase();
            const n2 = p2.name.toLowerCase();
            if (orderAlphabetically) {
              if (n1 < n2) {
                return -1;
              }
              if (n1 > n2) {
                return 1;
              }
              return 0;
            } else {
              if (n1 > n2) {
                return -1;
              }
              if (n1 < n2) {
                return 1;
              }
              return 0;
            }
          }),
      }));
    }
    setDataByCountries(result);
  }, [gips, countries, selectedCountries, orderAlphabetically]);

  const orderList = () => {
    const newOrder = !orderAlphabetically;
    setOrderAlphabetically(newOrder);
    dataLayer.push({
      event: "cefic_gip_map.project_list.sort",
      direction: newOrder ? "asc" : "desc",
    });
  };
  return (
    <>
      {!showProjectList && (
        <div
          onClick={() => {
            dataLayer.push({
              event: "cefic_gip_map.project_list.toggle_visibilty",
              visible: true,
            });
            setShowProjectList(true);
          }}
          className="cursor-pointer text-primary rounded-full absolute bg-white p-3 right-4 top-4 w-[56px] text-center text-2xl duration-300 hover:bg-primary hover:text-white z-500 hidden sm:block"
        >
          <FontAwesomeIcon icon={faList} />
        </div>
      )}
      {(showProjectList || isMobile) && (
        <div className="sm:h-full sm:absolute sm:right-0 sm:top-0 sm:pt-4 sm:pb-4 sm:pr-4 z-1100">
          <div
            className={`bg-white grow rounded-lg shadow-lg absolute transition-all duration-500 sm:static bottom-[-100%] ${
              showProjectList ? "!bottom-[-1%] sm:!bottom-inherit" : ""
            } h-full z-700 overflow-hidden w-full sm:w-80 lg:w-96 font-lato`}
          >
            <div className="flex justify-between font-lato text-xs px-4 py-2 sm:py-4 items-center">
              <p
                className="text-primary cursor-pointer"
                onClick={() => {
                  orderList();
                }}
              >
                Order A-Z
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`ml-2 text-gray-300 font-bold transform transition duration-100 ${
                    !orderAlphabetically ? "rotate-180" : "rotate-0"
                  }`}
                />
              </p>
              <p
                className="text-primary cursor-pointer"
                onClick={() => {
                  dataLayer.push({
                    event: "cefic_gip_map.project_list.toggle_visibilty",
                    visible: false,
                  });
                  setShowProjectList(false);
                }}
              >
                <span className="hidden sm:block">Hide projects list</span>
                <span className="block text-2xl sm:hidden">
                  <FontAwesomeIcon icon={faXmark} />
                </span>
              </p>
            </div>
            <div className="custom-scrollbar overflow-y-scroll h-full sm:pb-20 pb-0">
              {!dataByCountries.length && (
                <div className="border-t-1 border-gray-100 pt-6">
                  <div className="flex flex-col mb-4 pl-4 pr-4 pt-4 text-center text-primary">
                    <FontAwesomeIcon
                      icon={faLocationQuestion}
                      className="text-4xl"
                    />
                    <span className="font-lato text-sm font-bold mt-2">
                      No results
                    </span>
                    <span className="font-lato text-gray-400 text-xs mt-2">
                      Maybe try with less filters...
                    </span>
                  </div>
                </div>
              )}
              {dataByCountries?.map((country) => (
                <div
                  className="border-t-1 border-gray-100 mt-2"
                  key={`${country.id}`}
                >
                  <div className="flex justify-between pl-6 pr-4 pt-4 pb-[0.4rem]">
                    <div className="flex">
                      <Flag
                        code={country.acf?.country_code}
                        width="20"
                        height="15"
                        className="mr-2"
                      />
                      <span className="uppercase text-gray-400 tracking-widest font-bold text-[0.8rem]">
                        {country.name}
                      </span>
                    </div>
                    <span className="text-gray-300 font-bold text-xs">
                      {country.projects.length}{" "}
                      {country.projects.length === 1 ? "project" : "projects"}
                    </span>
                  </div>
                  <div>
                    {country.projects.map((p, index) => (
                      <div
                        key={`${p.id}-${country.id}-${index}`}
                        className={`cursor-pointer hover:bg-primary-100 group py-[0.375rem] pr-3 pl-6`}
                        onClick={() => {
                          setActiveProject(p.id, country.id);
                          map.flyTo(p.position, 7);
                          setShowProjectList(false);
                          dataLayer.push({
                            event: "cefic_gip_map.project_list.project.click",
                            projectId: p.id,
                            project: p.name,
                            countryId: country.id,
                            country: country.name,
                          });
                        }}
                      >
                        <Project {...p} countryId={country.id} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default List;
